/*
 * Flex Recordings, js/app.js
 * ------------------------------------------------------------------------
 * UI wiring. Depends on the globals exposed by js/data.js (FlexData, and the
 * FLEX_SEED fallback) and js/audio.js (FlexAudio). No imports, no modules.
 *
 * DOM contract: every id/class driven here comes from SPEC.md, index.html
 * and css/styles.css own the shell; this file only fills and reads it.
 * ------------------------------------------------------------------------
 */
(function () {
  'use strict';

  var REDUCED_MOTION = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

  var state = { filter: 'all', query: '' };
  var releases = [];
  var audioState = { playing: false, release: null, trackIndex: null }; // mirrors FlexAudio "state"
  var modalRelease = null;
  var lastFocused = null;
  var io = null;
  var swapTimer = null;
  var searchTimer = null;
  var toastTimer = null;
  var closeTimer = null;

  var els = {};

  /* ---- tiny helpers ---------------------------------------------------- */

  function byId(id) { return document.getElementById(id); }

  function setText(node, text) {
    if (node) node.textContent = text == null ? '' : String(text);
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function fmtTime(v) {
    if (window.FlexData && window.FlexData.format) return window.FlexData.format.fmtDuration(v);
    return '0:00';
  }

  function typeBadge(type) {
    if (window.FlexData && window.FlexData.format) return window.FlexData.format.typeBadge(type);
    return String(type || '').toUpperCase();
  }

  function pluralTracks(n) { return n === 1 ? '1 track' : n + ' tracks'; }

  function hasDirectAudio(r) {
    return Boolean(r && (r.audioUrl || (r.tracks || []).some(function (t) { return t && t.audioUrl; })));
  }

  function releaseById(id) {
    for (var i = 0; i < releases.length; i++) {
      if (releases[i].id === id) return releases[i];
    }
    return null;
  }

  /* ---- inline SVG icons (currentColor, 1.6 stroke) ---------------------- */

  var ICON_PLAY = '<svg class="icon-play" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round" stroke-linecap="round" aria-hidden="true"><path d="M8.2 5.6v12.8L19 12Z"/></svg>';
  var ICON_PAUSE = '<svg class="icon-pause" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" aria-hidden="true"><path d="M8.6 5.8v12.4M15.4 5.8v12.4"/></svg>';
  var ICON_SHARE = '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="6" cy="12" r="2.4"/><circle cx="17.5" cy="5.5" r="2.4"/><circle cx="17.5" cy="18.5" r="2.4"/><path d="M8.2 10.9l7.1-4.1M8.2 13.1l7.1 4.1"/></svg>';

  /* ---- element cache ---------------------------------------------------- */

  function cacheElements() {
    els.header = document.querySelector('.site-header');
    els.navToggle = byId('nav-toggle');
    els.nav = (els.header && els.header.querySelector('nav')) || document.querySelector('.site-nav');
    els.deck = byId('deck');
    els.empty = byId('empty');
    els.emptyReset = byId('empty-reset');
    els.pills = byId('pills');
    els.search = byId('search');
    els.toast = byId('toast');

    els.modalRoot = byId('modal-root');
    if (els.modalRoot) {
      els.modal = els.modalRoot.querySelector('.modal');
      els.modalClose = els.modalRoot.querySelector('.modal-close');
      els.modalArtWrap = els.modalRoot.querySelector('.modal-art-wrap');
      els.modalArtBlur = els.modalRoot.querySelector('.modal-art-blur');
    }
    els.modalArt = byId('modal-art');
    els.modalCat = byId('modal-cat');
    els.modalTitle = byId('modal-title');
    els.modalArtist = byId('modal-artist');
    els.modalMeta = byId('modal-meta');
    els.modalDesc = byId('modal-desc');
    els.modalEmbed = byId('modal-embed');
    els.modalNopreview = byId('modal-nopreview');
    els.modalTracklist = byId('modal-tracklist');
    els.modalBandcamp = byId('modal-bandcamp');
    els.modalBuy = byId('modal-buy');
    els.modalShare = byId('modal-share');

    els.miniPlayer = byId('mini-player');
    els.miniArt = byId('mini-art');
    els.miniTitle = byId('mini-title');
    els.miniArtist = byId('mini-artist');
    els.miniToggle = byId('mini-toggle');
    els.miniSeek = byId('mini-seek');
    els.miniTime = byId('mini-time');
    els.miniClose = byId('mini-close');
    els.scrollsCta = byId('scrolls-cta');
    els.scrollsCount = byId('scrolls-count');
    els.playlistToggle = byId('playlist-toggle');
    els.playlistDrawer = byId('playlist');
    els.playlistList = byId('playlist-list');
    els.playlistEmpty = byId('playlist-empty');
    els.playlistCount = byId('playlist-count');
    els.playlistClose = byId('playlist-close');
    els.playlistClear = byId('playlist-clear');
    els.playlistCopy = byId('playlist-copy');
    els.modalAdd = byId('modal-add');
  }

  /* ---- filtering + card rendering --------------------------------------- */

  function matches(release) {
    if (state.filter !== 'all' && release.type !== state.filter) return false;
    var q = state.query.trim().toLowerCase();
    if (!q) return true;
    var hay = [release.title, release.artist, release.catalogNumber, release.id, (release.tags || []).join(' ')]
      .filter(Boolean).join(' ').toLowerCase();
    if (hay.indexOf(q) !== -1) return true;
    // Most artists only appear at track level (DJ Sappo, Deadly Dee, ...), 
    // search must reach into the tracklist, not just the release artist field.
    var tracks = release.tracks || [];
    for (var i = 0; i < tracks.length; i++) {
      var trackHay = ((tracks[i].title || '') + ' ' + (tracks[i].artist || '')).toLowerCase();
      if (trackHay.indexOf(q) !== -1) return true;
    }
    return false;
  }

  var STORE_PRODUCTS = {
    '30flexr': '/products/various-artists-flex-30r',
    'flex-040-ricky-force-real-love-nice-n-easy': '/products/ricky-force-real-love-nice-n-easy-flex-40-vinyl',
    'black-hole': '/products/black-hole-double-pack'
  };

  function shareRelease(r) {
    var store = STORE_PRODUCTS[r.id];
    var url = store ? 'https://www.flexdnb.co.uk' + store : (r.bandcampUrl || 'https://www.flexdnb.co.uk/');
    var text = r.title + ' on Flex Recordings';
    if (navigator.share) {
      navigator.share({ title: r.title, text: text, url: url }).catch(function () {});
    } else if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text + ' ' + url).then(function () { toast('Link copied to clipboard'); }, function () {});
    } else {
      toast('Share from your browser: ' + url);
    }
  }

  // Bandcamp art CDN size variants, measured against the live CDN on 2026-10-16:
  //   _2 = 350px | _16 = 700px | _10 = 1200px (full) | _5 = 700px | _7 = 150px
  // _7 is TINY. It is correct only for the mini player and playlist thumbs,
  // which render at 40-56px. Release cards render a ~396px wide box, so they
  // must use artSrcset() below or they upscale a 150px image and look soft,
  // worst of all on Retina where the upscale is 5x.
  function artSize(url, size) {
    if (!url) return url;
    return url.replace(/_(\d+)\.jpg$/, '_' + size + '.jpg');
  }

  // Card artwork: let the browser pick by device pixel ratio and viewport
  // instead of hardcoding one tiny variant for everyone.
  //
  // Bandcamp only publishes SQUARE renditions, but the card art box is very wide:
  // full card width x 116px on mobile, and 58% of card width x card height
  // above 760px (the card is aspect-ratio 32/9, so that box is 2.06:1).
  // Feeding squares to object-fit:cover pays for roughly 70% of the pixels it
  // then throws away, which is what the uses-responsive-images audit measures.
  // So each breakpoint gets a pre-cropped WebP of its own shape, served by
  // images.weserv.nl, already a dependency for the accent sampler below.
  // Aspect values: 116/392 mobile, 388/800 desktop.
  var ART_SHAPES = {
    m: [[400, 118], [704, 208], [800, 237], [1200, 355]],
    d: [[400, 194], [800, 388], [1200, 582]]
  };
  // Rendered widths derived from the CSS grid and .card-art width, so the
  // browser picks the smallest rendition that still covers the real box.
  var ART_SIZES_MOBILE = 'calc(100vw - 20px)';
  var ART_SIZES_DESKTOP =
    '(min-width:761px) and (max-width:1023px) calc((100vw - 48px) * 0.58),' +
    ' (min-width:1024px) and (max-width:1379px) calc((100vw - 64px) * 0.29),' +
    ' (min-width:1380px) 381px';
  function artSrcset(url, mode) {
    if (!url) return '';
    var src = encodeURIComponent(url);
    var shapes = ART_SHAPES[mode] || ART_SHAPES.d;
    return shapes.map(function (d) {
      return 'https://images.weserv.nl/?url=' + src + '&w=' + d[0] + '&h=' + d[1] +
        '&fit=cover&output=webp&q=75 ' + d[0] + 'w';
    }).join(', ');
  }

  function cardHtml(r, index) {
    var cat = String(r.catalogNumber || r.id || '').toUpperCase();
    var hasArt = Boolean(r.artwork);
    var metaParts = [];
    if (r.year != null) metaParts.push(String(r.year));
    if (r.tracks && r.tracks.length) metaParts.push(pluralTracks(r.tracks.length));
    var img = hasArt
      ? '<picture>' +
        '<source type="image/webp" media="(max-width:760px)"' +
          ' sizes="' + ART_SIZES_MOBILE + '"' +
          ' srcset="' + esc(artSrcset(r.artwork, 'm')) + '">' +
        '<source type="image/webp" media="(min-width:761px)"' +
          ' sizes="' + ART_SIZES_DESKTOP + '"' +
          ' srcset="' + esc(artSrcset(r.artwork, 'd')) + '">' +
        // src stays the Bandcamp original: it is the no-WebP fallback and the
        // input the accent sampler reads. onArtError retries against it.
        '<img class="card-art-img" loading="lazy" decoding="async"' +
          ' fetchpriority="low" referrerpolicy="no-referrer"' +
          ' src="' + esc(artSize(r.artwork, 2)) + '" alt="">' +
        '</picture>'
      : '';
    var label = 'Open ' + cat + ' · ' + r.title;
    var styleAttr = REDUCED_MOTION ? '' : ' style="transition-delay:' + (index % 10) * 30 + 'ms"';

    return '<li class="card-wrap">' +
      '<div class="card reveal' + (hasArt ? ' has-art' : '') + '" data-id="' + esc(r.id) + '" data-type="' + esc(r.type) + '"' +
      ' tabindex="0" role="button" aria-haspopup="dialog" aria-label="' + esc(label) + '"' + styleAttr + '>' +
        (STORE_PRODUCTS[r.id]
          ? '<span class="card-vinyl-ribbon" aria-hidden="true">Vinyl</span>'
          : '') +
        '<div class="card-art" aria-hidden="true">' + img +
          '<div class="card-art-fallback" aria-hidden="true"><span>F</span></div>' +
        '</div>' +
        '<div class="card-scrim" aria-hidden="true"></div>' +
        '<div class="card-body">' +
          '<div class="card-top"><span class="card-cat">' + esc(cat) + '</span>' +
            (STORE_PRODUCTS[r.id] ? '' : '<span class="card-type">' + esc(typeBadge(r.type)) + '</span>') +
          '</div>' +
          '<div class="card-titles"><h3 class="card-title">' + esc(r.title) + '</h3><p class="card-artist">' + esc(r.artist) + '</p></div>' +
          '<div class="card-bottom">' +
            '<span class="card-meta">' + esc(metaParts.join(' · ')) + '</span>' +
            '<span class="card-actions">' +
              (STORE_PRODUCTS[r.id]
                ? '<a class="card-buy" href="https://www.flexdnb.co.uk' + esc(STORE_PRODUCTS[r.id]) + '" aria-label="Buy Vinyl: ' + esc(r.title) + '">Buy Vinyl</a>'
                : '') +
              '<button class="card-share" type="button" data-share aria-label="Share ' + esc(r.title) + '">' + ICON_SHARE + '</button>' +
              '<button class="card-add" type="button" data-add aria-pressed="false" aria-label="Add ' + esc(r.title) + ' to playlist">+</button>' +
            '</span>' +
            '<button class="play-btn" type="button" data-play aria-label="Play ' + esc(r.title) + '">' + ICON_PLAY + ICON_PAUSE + '</button>' +
          '</div>' +
        '</div>' +
        '<div class="card-eq" aria-hidden="true"><i></i><i></i><i></i><i></i></div>' +
      '</div>' +
    '</li>';
  }

  function observeReveals() {
    if (!els.deck) return;
    var nodes = els.deck.querySelectorAll('.reveal');
    if (REDUCED_MOTION || !('IntersectionObserver' in window)) {
      nodes.forEach(function (n) { n.classList.add('is-visible'); });
      return;
    }
    if (io) io.disconnect();
    io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    nodes.forEach(function (n) {
      if (!n.classList.contains('is-visible')) io.observe(n);
    });
  }

  var PAGE_SIZE = 12;
  var shown = PAGE_SIZE;

  function updateShowMore(total) {
    var wrap = document.getElementById('show-more-wrap');
    if (!wrap) return;
    wrap.hidden = total <= shown;
    var btn = document.getElementById('show-more');
    if (btn) {
      var left = total - shown;
      btn.textContent = 'Show more releases (' + left + ' left)';
    }
  }

  function render() {
    if (!els.deck) return;
    var visible = releases.filter(matches);
    var limited = visible.slice(0, shown);
    els.deck.innerHTML = limited.map(cardHtml).join('');
    if (els.empty) els.empty.hidden = visible.length !== 0;
    updateShowMore(visible.length);
    observeReveals();
    syncPlayingCards();
    applyArtAccents();
  }

  /* ---- per-card accent colour, sampled from the release artwork ---- */
  function sampleAccent(img, card) {
    if (!img.complete || !img.naturalWidth) return;
    try {
      var c = document.createElement('canvas');
      c.width = 16; c.height = 16;
      var ctx = c.getContext('2d', { willReadFrequently: true });
      ctx.drawImage(img, 0, 0, 16, 16);
      var data = ctx.getImageData(0, 0, 16, 16).data;
      var buckets = {};
      for (var i = 0; i < data.length; i += 4) {
        var r = data[i] / 255, g = data[i + 1] / 255, b = data[i + 2] / 255;
        var mx = Math.max(r, g, b), mn = Math.min(r, g, b);
        var sat = mx === 0 ? 0 : (mx - mn) / mx;
        var light = (mx + mn) / 2;
        var weight = sat * (1 - Math.abs(light - 0.5) * 1.2); // favour vivid mid-bright pixels
        if (weight <= 0.05) continue;
        var hue;
        if (mx === r) hue = ((g - b) / (mx - mn)) % 6;
        else if (mx === g) hue = (b - r) / (mx - mn) + 2;
        else hue = (r - g) / (mx - mn) + 4;
        var bucket = Math.round(((hue * 60) + 360) % 360 / 30) % 12;
        if (!buckets[bucket]) buckets[bucket] = { r: 0, g: 0, b: 0, n: 0 };
        var bk = buckets[bucket];
        bk.r += r * 255; bk.g += g * 255; bk.b += b * 255; bk.n++;
      }
      var best = null;
      for (var key in buckets) if (!best || buckets[key].n > best.n) best = buckets[key];
      if (best && best.n >= 4) {
        card.style.setProperty('--card-accent',
          'rgb(' + Math.round(best.r / best.n) + ',' + Math.round(best.g / best.n) + ',' + Math.round(best.b / best.n) + ')');
      }
    } catch (err) { /* CORS-tainted canvas, card keeps the brand-blue fallback */ }
  }
  function applyArtAccents() {
    document.querySelectorAll('.card.has-art').forEach(function (card) {
      var img = card.querySelector('.card-art-img');
      var src = img && img.getAttribute('src');
      if (!src) return;
      // Bandcamp's CDN sends no CORS headers, so pixel reads go through the
      // weserv image proxy (CORS-open, 24px rendition). Failure = brand blue fallback.
      var probe = new Image();
      probe.crossOrigin = 'anonymous';
      probe.onload = function () { sampleAccent(probe, card); };
      probe.src = 'https://images.weserv.nl/?url=' + encodeURIComponent(src) + '&w=24&h=24&fit=cover&output=jpg&q=75';
    });
  }

  function renderWithFade() {
    shown = PAGE_SIZE;
    if (!els.deck) { render(); return; }
    clearTimeout(swapTimer);
    els.deck.classList.add('is-swapping'); // CSS: opacity .2, 150ms
    swapTimer = setTimeout(function () {
      render();
      els.deck.classList.remove('is-swapping');
    }, 150);
  }

  function updatePillCounts() {
    if (els.scrollsCount) {
      els.scrollsCount.textContent = '(' + releases.filter(function (r) { return r.type === 'archive'; }).length + ')';
    }
    if (!els.pills) return;
    var counts = { all: releases.length };
    releases.forEach(function (r) { counts[r.type] = (counts[r.type] || 0) + 1; });
    els.pills.querySelectorAll('.pill-count').forEach(function (span) {
      var key = span.getAttribute('data-count');
      span.textContent = String(counts[key] || 0);
    });
  }

  function setPillActive(filter) {
    if (!els.pills) return;
    els.pills.querySelectorAll('.pill').forEach(function (btn) {
      btn.classList.toggle('is-active', btn.getAttribute('data-filter') === filter);
    });
  }

  /* ---- toast ------------------------------------------------------------- */

  function toast(message) {
    if (!els.toast) return;
    clearTimeout(toastTimer);
    els.toast.textContent = message;
    els.toast.classList.add('is-visible');
    toastTimer = setTimeout(function () {
      els.toast.classList.remove('is-visible');
    }, 2600);
  }

  /* ---- modal -------------------------------------------------------------- */

  function openModal(release) {
    if (!release || !els.modalRoot) return;
    modalRelease = release;
    syncModalAdd();
    var cat = String(release.catalogNumber || release.id || '').toUpperCase();

    setText(els.modalCat, cat);
    setText(els.modalTitle, release.title);
    setText(els.modalArtist, release.artist);

    var metaParts = [];
    if (release.year != null) metaParts.push(String(release.year));
    metaParts.push(typeBadge(release.type));
    if (release.tracks && release.tracks.length) metaParts.push(pluralTracks(release.tracks.length));
    if (release.price) metaParts.push(release.price);
    setText(els.modalMeta, metaParts.join(' · '));

    if (release.description) {
      setText(els.modalDesc, release.description);
      els.modalDesc.hidden = false;
    } else {
      setText(els.modalDesc, '');
      els.modalDesc.hidden = true;
    }

    if (release.artwork) {
      if (els.modalArt) { els.modalArt.src = release.artwork; els.modalArt.hidden = false; }
      if (els.modalArtWrap) els.modalArtWrap.classList.remove('no-art');
      if (els.modalArtBlur) els.modalArtBlur.style.backgroundImage = 'url("' + artSize(release.artwork, 7) + '")';
    } else {
      if (els.modalArt) { els.modalArt.removeAttribute('src'); els.modalArt.hidden = true; }
      if (els.modalArtWrap) els.modalArtWrap.classList.add('no-art');
      if (els.modalArtBlur) els.modalArtBlur.style.backgroundImage = '';
    }

    var playable = hasDirectAudio(release);

    if (release.embedId) {
      var playerTitle = release.title + ' · Bandcamp player';
      els.modalEmbed.innerHTML =
        '<iframe src="https://bandcamp.com/EmbeddedPlayer/album=' + encodeURIComponent(String(release.embedId)) +
        '/size=large/artwork=small/transparent=true/" width="100%" height="240" loading="lazy"' +
        ' allow="autoplay; clipboard-write; encrypted-media" title="' + esc(playerTitle) + '"></iframe>';
      els.modalEmbed.hidden = false;
    } else {
      els.modalEmbed.innerHTML = '';
      els.modalEmbed.hidden = true;
    }

    if (els.modalNopreview) els.modalNopreview.hidden = Boolean(release.embedId) || playable;

    // tracklist (read-only: playback lives in the Bandcamp embed above)
    var sameReleasePlaying = audioState.release && audioState.release.id === release.id;
    els.modalTracklist.innerHTML = (release.tracks || []).map(function (t, i) {
      var num = String(i + 1).padStart(2, '0');
      var liPlaying = sameReleasePlaying && audioState.playing && audioState.trackIndex === i ? ' is-playing' : '';
      var artist = (t && t.artist) ? ' <span class="track-artist">· ' + esc(t.artist) + '</span>' : '';
      return '<li class="track' + liPlaying + '" data-track="' + i + '">' +
        '<span class="track-num">' + esc(num) + '</span>' +
        '<span class="track-name">' + esc(t.title) + artist + '</span>' +
        '<span class="track-dur">' + esc((t && t.duration) || '') + '</span>' +
      '</li>';
    }).join('');

    var url = release.bandcampUrl || 'https://flexindustries.bandcamp.com';
    var storePath = STORE_PRODUCTS[release.id];
    if (els.modalBandcamp) {
      els.modalBandcamp.href = url;
      // Store-backed vinyl leads with the store buy; Bandcamp stays as the listen link.
      els.modalBandcamp.classList.toggle('btn-primary', !storePath);
      els.modalBandcamp.classList.toggle('btn-ghost', Boolean(storePath));
    }
    if (els.modalBuy) {
      if (storePath) {
        els.modalBuy.href = 'https://www.flexdnb.co.uk' + storePath;
        els.modalBuy.textContent = 'Buy Vinyl';
        els.modalBuy.classList.add('btn-primary');
        els.modalBuy.classList.remove('btn-ghost');
      } else {
        els.modalBuy.href = url;
        els.modalBuy.textContent = 'Buy now';
        els.modalBuy.classList.remove('btn-primary');
        els.modalBuy.classList.add('btn-ghost');
      }
    }
    if (els.modalShare) {
      els.modalShare.onclick = function () { shareRelease(release); };
    }

    // open (remove [hidden] -> force reflow -> .is-open for the transition)
    clearTimeout(closeTimer);
    lastFocused = document.activeElement;
    els.modalRoot.hidden = false;
    void els.modalRoot.offsetWidth;
    els.modalRoot.classList.add('is-open');
    document.body.classList.add('modal-open');
    syncModalPlayback();
    if (els.modalClose) els.modalClose.focus();
  }

  function closeModal() {
    if (!els.modalRoot || els.modalRoot.hidden) return;
    els.modalRoot.classList.remove('is-open');
    document.body.classList.remove('modal-open');
    closeTimer = setTimeout(function () {
      if (els.modalRoot && !els.modalRoot.classList.contains('is-open')) els.modalRoot.hidden = true;
    }, REDUCED_MOTION ? 0 : 260);
    if (lastFocused && typeof lastFocused.focus === 'function') {
      try { lastFocused.focus(); } catch (e) { /* node may be gone after re-render */ }
    }
    lastFocused = null;
    modalRelease = null;
  }

  /* ---- playback helpers ---------------------------------------------------- */

  function playRelease(release, trackIndex) {
    if (!window.FlexAudio || !release) return;
    var sameRelease = audioState.release && audioState.release.id === release.id;
    if (sameRelease && (trackIndex == null || trackIndex === audioState.trackIndex)) {
      FlexAudio.toggle();
      return;
    }
    FlexAudio.loadAndPlay(release, trackIndex == null ? 0 : trackIndex).then(function (res) {
      if (res && res.ok) toast('Playing preview');
    });
  }

  function onDeckClick(e) {
    var shareBtn = e.target.closest('[data-share]');
    if (shareBtn) {
      e.stopPropagation();
      var shareCard = shareBtn.closest('.card');
      var shareRel = releaseById(shareCard && shareCard.getAttribute('data-id'));
      if (shareRel) shareRelease(shareRel);
      return;
    }
    var addBtn = e.target.closest('[data-add]');
    if (addBtn) {
      e.stopPropagation();
      var addCard = addBtn.closest('.card');
      var addRel = releaseById(addCard && addCard.getAttribute('data-id'));
      if (addRel) togglePlaylist(addRel);
      return;
    }
    var playBtn = e.target.closest('[data-play]');
    if (playBtn) {
      e.stopPropagation();
      var card = playBtn.closest('.card');
      var release = releaseById(card && card.getAttribute('data-id'));
      if (!release) return;
      if (hasDirectAudio(release)) {
        playRelease(release, null);
      } else {
        openModal(release);
        toast('No preview. Listen on Bandcamp');
      }
      return;
    }
    var article = e.target.closest('.card');
    if (article) {
      var rel = releaseById(article.getAttribute('data-id'));
      if (rel) openModal(rel);
    }
  }

  function onDeckKeydown(e) {
    if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
    if (!e.target.closest) return;
    if (e.target.closest('[data-play]')) return; // native button handles Enter/Space
    var card = e.target.closest('.card');
    if (!card) return;
    e.preventDefault();
    var release = releaseById(card.getAttribute('data-id'));
    if (release) openModal(release);
  }

  /* ---- audio state -> UI ----------------------------------------------------- */

  function syncPlayingCards() {
    if (!els.deck) return;
    var playingId = audioState.release ? audioState.release.id : null;
    els.deck.querySelectorAll('.card').forEach(function (card) {
      card.classList.toggle('is-playing', Boolean(playingId && card.getAttribute('data-id') === playingId));
    });
  }

  function syncModalPlayback() {
    if (!els.modalRoot || els.modalRoot.hidden) return;
    var same = modalRelease && audioState.release && modalRelease.id === audioState.release.id;
    if (els.modalTracklist) {
      els.modalTracklist.querySelectorAll('.track').forEach(function (li) {
        var i = Number(li.getAttribute('data-track'));
        li.classList.toggle('is-playing', Boolean(same && audioState.playing && audioState.trackIndex === i));
      });
    }
  }

  function wireAudio() {
    if (!window.FlexAudio) return;

    FlexAudio.on('time', function (p) {
      var pct = String(Math.round((p.ratio || 0) * 1000));
      var now = fmtTime(p.current) + ' / ' + fmtTime(p.duration);
      if (els.miniSeek) els.miniSeek.value = pct;
      setText(els.miniTime, now);
    });

    FlexAudio.on('state', function (p) {
      audioState = p;
      var release = p.release;
      if (release && els.miniPlayer) {
        els.miniPlayer.classList.add('is-visible');
        if (release.artwork) {
          if (els.miniArt) { els.miniArt.src = artSize(release.artwork, 7); els.miniArt.hidden = false; }
        } else if (els.miniArt) {
          els.miniArt.removeAttribute('src');
          els.miniArt.hidden = true;
        }
        var track = (release.tracks || [])[p.trackIndex];
        setText(els.miniTitle, (track && track.title) || release.title);
        setText(els.miniArtist, release.artist);
        els.miniPlayer.classList.toggle('is-playing', Boolean(p.playing));
      } else if (els.miniPlayer) {
        els.miniPlayer.classList.remove('is-visible');
        els.miniPlayer.classList.remove('is-playing');
        if (els.miniSeek) els.miniSeek.value = '0';
        setText(els.miniTime, '0:00 / 0:00');
      }
      syncPlayingCards();
      syncModalPlayback();
    });

    FlexAudio.on('error', function (p) {
      toast((p && p.message) || 'Audio preview failed');
    });
  }

  /* ---- events ------------------------------------------------------------------ */

  /* ---- playlist (the listener's crate) ----------------------------------- */

  var PLAYLIST_KEY = 'flex-playlist-v1';
  var playlist = loadPlaylist();

  function loadPlaylist() {
    try {
      var raw = localStorage.getItem(PLAYLIST_KEY);
      var list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list.filter(function (x) { return typeof x === 'string'; }) : [];
    } catch (err) { return []; }
  }
  function savePlaylist() {
    try { localStorage.setItem(PLAYLIST_KEY, JSON.stringify(playlist)); } catch (err) { /* storage blocked */ }
  }
  function inPlaylist(id) { return playlist.indexOf(id) !== -1; }
  function togglePlaylist(release) {
    if (!release) return;
    var at = playlist.indexOf(release.id);
    if (at > -1) { playlist.splice(at, 1); toast('Removed from playlist'); }
    else { playlist.push(release.id); toast('Added to playlist'); }
    savePlaylist();
    renderPlaylist();
    syncAddButtons();
    syncModalAdd();
  }
  function syncAddButtons() {
    document.querySelectorAll('.card').forEach(function (card) {
      var btn = card.querySelector('[data-add]');
      if (!btn) return;
      var saved = inPlaylist(card.getAttribute('data-id'));
      btn.classList.toggle('is-saved', saved);
      btn.setAttribute('aria-pressed', saved ? 'true' : 'false');
    });
  }
  function syncModalAdd() {
    if (!els.modalAdd) return;
    var saved = modalRelease ? inPlaylist(modalRelease.id) : false;
    els.modalAdd.textContent = saved ? 'Saved ✓' : 'Add to playlist';
    els.modalAdd.classList.toggle('is-saved', saved);
  }
  function renderPlaylist() {
    if (els.playlistCount) els.playlistCount.textContent = String(playlist.length);
    if (!els.playlistList) return;
    var rows = '';
    playlist.forEach(function (id) {
      var r = releaseById(id);
      if (!r) return;
      rows += '<li class="playlist-row">' +
        '<button class="playlist-play" type="button" data-play-row="' + esc(id) + '" aria-label="Open ' + esc(r.title) + '">' +
        (r.artwork ? '<img src="' + esc(artSize(r.artwork, 7)) + '" alt="" loading="lazy" referrerpolicy="no-referrer">' : '<span class="playlist-thumb-fallback">F</span>') +
        '</button>' +
        '<span class="playlist-row-info"><span class="playlist-row-title">' + esc(r.title) + '</span>' +
        '<span class="playlist-row-artist">' + esc(r.artist || '') + '</span></span>' +
        '<button class="playlist-remove" type="button" data-remove-row="' + esc(id) + '" aria-label="Remove ' + esc(r.title) + ' from playlist">✕</button>' +
        '</li>';
    });
    els.playlistList.innerHTML = rows;
    if (els.playlistEmpty) els.playlistEmpty.hidden = playlist.length > 0;
    if (els.playlistClear) els.playlistClear.disabled = playlist.length === 0;
    if (els.playlistCopy) els.playlistCopy.disabled = playlist.length === 0;
  }
  function openPlaylist() {
    if (!els.playlistDrawer) return;
    els.playlistDrawer.hidden = false;
    requestAnimationFrame(function () { els.playlistDrawer.classList.add('is-open'); });
  }
  function closePlaylist() {
    if (!els.playlistDrawer) return;
    els.playlistDrawer.classList.remove('is-open');
    setTimeout(function () { els.playlistDrawer.hidden = true; }, 240);
  }

  function bindEvents() {
    if (els.deck) {
      els.deck.addEventListener('click', onDeckClick);
      els.deck.addEventListener('keydown', onDeckKeydown);
      // Card art is proxied through images.weserv.nl. If that proxy fails, drop
      // the <source> elements so the browser falls back to the Bandcamp JPEG on
      // the <img>, and only remove the image if that fails too. A failed
      // <source> does NOT fall through to the next source on its own.
      els.deck.addEventListener('error', function (e) {
        var node = e.target;
        if (!node || node.tagName !== 'IMG' || !node.classList.contains('card-art-img')) return;
        if (node.dataset.artFallback) { node.remove(); return; }
        node.dataset.artFallback = '1';
        var pic = node.parentNode;
        if (pic && pic.tagName === 'PICTURE') {
          Array.prototype.forEach.call(pic.querySelectorAll('source'), function (s) { s.remove(); });
        }
      }, true);
    }

    if (els.pills) {
      els.pills.addEventListener('click', function (e) {
        var btn = e.target.closest('.pill');
        if (!btn) return;
        var filter = btn.getAttribute('data-filter') || 'all';
        if (filter === state.filter) return;
        state.filter = filter;
        setPillActive(filter);
        renderWithFade();
      });
    }

    var showMoreBtn = document.getElementById('show-more');
    if (showMoreBtn) {
      showMoreBtn.addEventListener('click', function () {
        shown += PAGE_SIZE;
        render();
      });
    }

    if (els.scrollsCta) {
      els.scrollsCta.addEventListener('click', function () {
        if (state.filter !== 'archive') {
          state.filter = 'archive';
          setPillActive('archive');
          renderWithFade();
        }
        var bar = document.getElementById('music');
        if (bar) bar.scrollIntoView({ behavior: REDUCED_MOTION ? 'auto' : 'smooth', block: 'start' });
      });
    }

    // Hero EQ strip, finer, denser bands regenerated each load so it feels live
    var heroEq = document.querySelector('.hero-eq');
    if (heroEq && !heroEq.childElementCount) {
      var eqBars = '';
      for (var qi = 0; qi < 72; qi++) {
        eqBars += '<i style="--h:' + (0.12 + Math.random() * 0.88).toFixed(2) +
          ';--d:-' + (Math.random() * 2).toFixed(2) + 's;--t:' + (1.1 + Math.random() * 1.3).toFixed(2) + 's"></i>';
      }
      heroEq.innerHTML = eqBars;
    }

    if (els.playlistToggle) els.playlistToggle.addEventListener('click', openPlaylist);
    if (els.playlistClose) els.playlistClose.addEventListener('click', closePlaylist);
    if (els.playlistClear) els.playlistClear.addEventListener('click', function () {
      playlist = [];
      savePlaylist();
      renderPlaylist();
      syncAddButtons();
      syncModalAdd();
      toast('Playlist cleared');
    });
    if (els.playlistCopy) els.playlistCopy.addEventListener('click', function () {
      var links = playlist.map(function (id) {
        var r = releaseById(id);
        return r ? r.bandcampUrl : null;
      }).filter(Boolean);
      var text = links.join('\n');
      var done = function () { toast('Playlist links copied'); };
      if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(text).then(done, done);
      else done();
    });
    if (els.playlistList) {
      els.playlistList.addEventListener('click', function (e) {
        var rm = e.target.closest('[data-remove-row]');
        if (rm) {
          var rel = releaseById(rm.getAttribute('data-remove-row'));
          if (rel) togglePlaylist(rel);
          return;
        }
        var op = e.target.closest('[data-play-row]');
        if (op) {
          var rel2 = releaseById(op.getAttribute('data-play-row'));
          if (rel2) { closePlaylist(); openModal(rel2); }
        }
      });
    }
    if (els.modalAdd) els.modalAdd.addEventListener('click', function () {
      if (modalRelease) togglePlaylist(modalRelease);
    });

    if (els.search) {
      els.search.addEventListener('input', function () {
        clearTimeout(searchTimer);
        searchTimer = setTimeout(function () {
          state.query = els.search.value || '';
          renderWithFade();
        }, 150);
      });
    }

    if (els.emptyReset) {
      els.emptyReset.addEventListener('click', function () {
        state.filter = 'all';
        state.query = '';
        if (els.search) els.search.value = '';
        setPillActive('all');
        renderWithFade();
      });
    }

    if (els.modalRoot) {
      els.modalRoot.addEventListener('click', function (e) {
        if (e.target.closest('[data-modal-close]')) closeModal();
      });
    }

    if (els.miniToggle) {
      els.miniToggle.addEventListener('click', function () {
        if (window.FlexAudio) FlexAudio.toggle();
      });
    }
    if (els.miniClose) {
      els.miniClose.addEventListener('click', function () {
        if (window.FlexAudio) FlexAudio.stop();
      });
    }

    var bindSeek = function (input) {
      if (!input) return;
      input.addEventListener('input', function () {
        if (window.FlexAudio) FlexAudio.seekRatio(Number(input.value) / 1000);
      });
    };
    bindSeek(els.miniSeek);

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && els.modalRoot && !els.modalRoot.hidden) closeModal();
    });

    if (els.header) {
      window.addEventListener('scroll', function () {
        els.header.classList.toggle('is-scrolled', window.scrollY > 8);
      }, { passive: true });
    }

    if (els.navToggle) {
      if (!els.navToggle.hasAttribute('aria-expanded')) els.navToggle.setAttribute('aria-expanded', 'false');
      els.navToggle.addEventListener('click', function () {
        var open = els.nav ? els.nav.classList.toggle('is-open') : false;
        els.navToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    }
  }

  /* ---- boot ----------------------------------------------------------------------- */

  /* ---- catalog schema for search engines & LLMs (SEO/GEO) ----------------- */
  function injectCatalogSchema() {
    var old = document.getElementById('flex-catalog-schema');
    if (old) old.remove();
    var ld = {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      'name': 'Flex Recordings catalog',
      'numberOfItems': releases.length,
      'itemListElement': releases.map(function (r, i) {
        return {
          '@type': 'ListItem',
          'position': i + 1,
          'item': {
            '@type': 'MusicRelease',
            'name': r.title,
            'byArtist': { '@type': 'MusicGroup', 'name': r.artist || 'Flex Recordings' },
            'datePublished': r.date || (r.year ? String(r.year) : undefined),
            'url': r.bandcampUrl
          }
        };
      })
    };
    var s = document.createElement('script');
    s.type = 'application/ld+json';
    s.id = 'flex-catalog-schema';
    s.textContent = JSON.stringify(ld);
    document.head.appendChild(s);
  }

  function init(data) {
    releases = Array.isArray(data) ? data : [];
    cacheElements();
    updatePillCounts();
    renderPlaylist();
    syncAddButtons();
    injectCatalogSchema();
    wireAudio();
    bindEvents();
    render();
  }

  function boot() {
    if (window.FlexData && window.FlexData.ready && typeof window.FlexData.ready.then === 'function') {
      window.FlexData.ready.then(init).catch(function (err) {
        console.error('[Flex] failed to initialise data', err);
      });
    } else {
      console.error('[Flex] FlexData.ready is missing, is js/data.js loaded before js/app.js?');
    }
  }

  boot();
})();
